import { uploadFileApi } from "@/api/services/global/file";
import { IconClose, IconFile } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { CardContainer } from "@/components/commons/CardContainer";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { DropZone } from "@/components/elements/DropZone";
import { FormField } from "@/components/elements/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { Input } from "@/components/elements/Input";
import { FILE_UPLOAD_FOR } from "@/constants/file";
import { TSubjectCreateForm } from "@/features/learning-content/types/learningContent";
import { useDialog } from "@/hooks/useDialog";
import { TExtendFile } from "@/types/file";
import { convertByteToMb, getFileNameFromPath, getPdfPageCount } from "@/utils/file";
import { showError } from "@/utils/error";
import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import { FC, useCallback, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

type TTextExplanationFormProps = {};

// Text explanation form section for subject creation
// Threshold for small PDF warning (100KB)
const SMALL_FILE_SIZE_THRESHOLD = 100 * 1024;

export const TextExplanationForm: FC<TTextExplanationFormProps> = () => {
  const { control, setValue } = useFormContext<TSubjectCreateForm>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<TExtendFile | null>(null);
  const pageCount = useWatch({ control, name: "textExplanationPageCount" });
  const existingFileUrl = useWatch({ control, name: "textExplanationFileUrl" });
  const smallFileDialog = useDialog();
  const pendingFileRef = useRef<TExtendFile | null>(null);

  // Derive display name from existing file URL (edit mode)
  const existingFileName = existingFileUrl ? (getFileNameFromPath(existingFileUrl) ?? "file.pdf") : null;

  // Show file info if new upload or existing file from API
  const hasFile = !!uploadedFile || !!existingFileUrl;

  // Upload file to server after validation/confirmation
  const processFileUpload = useCallback(
    async (file: TExtendFile) => {
      const count = await getPdfPageCount(file);
      setValue("textExplanationPageCount", count);

      setIsUploading(true);
      try {
        const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.LEARNING_CONTENT_FILE });
        Object.assign(file, { filePath: rs.data.filePath });
        setUploadedFile(file);
        setValue("textExplanationFile", file, { shouldDirty: true });
        setValue("textExplanationFileUrl", null);
      } catch (error) {
        showError(error);
        setUploadedFile(null);
        setValue("textExplanationFile", null);
        setValue("textExplanationPageCount", 0);
      } finally {
        setIsUploading(false);
      }
    },
    [setValue],
  );

  // Handle file selection: check size, warn if small, then upload
  const handleFileChange = useCallback(
    async (value: TExtendFile | TExtendFile[] | null) => {
      const file = Array.isArray(value) ? value[0] : value;

      if (!file) {
        setUploadedFile(null);
        setValue("textExplanationFile", null);
        setValue("textExplanationPageCount", 0);
        return;
      }

      // Show warning for small files (<100KB)
      if (file.size < SMALL_FILE_SIZE_THRESHOLD) {
        pendingFileRef.current = file;
        const confirmed = await smallFileDialog.show();
        if (!confirmed) {
          pendingFileRef.current = null;
          return;
        }
        pendingFileRef.current = null;
      }

      await processFileUpload(file);
    },
    [setValue, smallFileDialog, processFileUpload],
  );

  // Remove uploaded or existing file
  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
    setValue("textExplanationFile", null, { shouldDirty: true });
    setValue("textExplanationFileUrl", null, { shouldDirty: true });
    setValue("textExplanationPageCount", 0);
  }, [setValue]);

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          テキスト解説
        </Typography>

        <DropZone
          nonControl
          enableFileList={false}
          options={{ multiple: false }}
          rules={{
            acceptMimeTypes: ["application/pdf"],
            maxSize: 10 * 1024 * 1024, // 10MB
          }}
          uploadFor={FILE_UPLOAD_FOR.LEARNING_CONTENT_FILE}
          onChange={handleFileChange}
          disabled={isUploading}
        >
          <Stack alignItems="center" gap={2}>
            <Typography variant="body14">アップロードするファイルをドラッグ&ドロップ</Typography>
            <Typography variant="body14">または</Typography>
            <Button variant="outline" size="sm">
              ファイルを選択する
            </Button>
            <Typography variant="body14" sx={{ color: "text.secondary", mt: 1 }}>
              ※10MB以下のPDFファイルのみアップロード可能
            </Typography>
          </Stack>
        </DropZone>

        {/* File info row: icon + name + size + delete + page count */}
        {hasFile && (
          <Stack direction="row" alignItems="center" gap={1}>
            <IconFile fontSize="32px" />
            <Typography variant="body14" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {uploadedFile ? uploadedFile.name : existingFileName}
            </Typography>
            {uploadedFile && (
              <Typography variant="body14" sx={{ whiteSpace: "nowrap", mr: 0.5 }}>
                {convertByteToMb(uploadedFile.size).toFixed(0)}MB
              </Typography>
            )}
            <IconBox onClick={handleRemoveFile}>
              <IconClose />
            </IconBox>
            <Input
              nonControl
              readonly
              value={pageCount || ""}
              sx={{ width: "120px", ml: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">ページ</InputAdornment>,
              }}
            />
          </Stack>
        )}

        <Box>
          <Typography variant="body14" sx={{ color: "text.secondary", fontSize: "12px", mb: 1 }}>
            テキスト解説に関する社内メモ
          </Typography>
          <FormField control={control} name="textExplanationInternalNote" label="">
            <Input fullWidth placeholder="入力してください" />
          </FormField>
        </Box>
      </Stack>

      {/* Warning modal for small PDF files (<100KB) */}
      <ConfirmDeleteModal
        open={smallFileDialog.open}
        onClose={smallFileDialog.cancel}
        onOk={smallFileDialog.confirm}
        message="小さなサイズのPDFを検知しました。内容に間違いがないか一度ご確認ください。このままアップロードしても良いですか？"
      />
    </CardContainer>
  );
};
